<template>
    <!--
      Digitizing / vectorizing charges for ONE tier (Trello #572).

      Jake, 2026-10-27/28: the distributor charges for digitizing and
      vectorizing are a SETTING ON THE SUBSCRIPTION TIER with two modes --
      charge immediately (the default) or add to the monthly subscription
      invoice. Three fields with meaningful half-edited states, so an
      explicit Save (the supplier list's idiom), not instant writes.

      Controlled by the parent (PlansPricing via PlanCard): `setting` is the
      server's row for this tier; the component keeps only a local DRAFT and
      emits `save` with the whole setting in cents.
    -->
    <div :data-testid="`sa-plan-card-art-services-${planId}`">
        <div class="art-services__head">
            <span class="art-services__title">
                Digitizing and vectorizing charges
            </span>
            <span class="art-services__hint">
                What distributors on this plan pay per digitizing job
                (embroidery) and per vectorizing job (DTF, screen print), and
                when. A change applies to jobs ordered after you save.
            </span>
        </div>

        <p
            v-if="!tierKey"
            class="art-services__empty"
            :data-testid="`sa-plan-card-art-services-unmapped-${planId}`"
        >
            Not shown for this plan: it has no pricing tier yet, and these
            charges are configured per tier.
        </p>

        <template v-else>
            <fieldset
                class="art-services__modes"
                :disabled="loading || saving"
            >
                <legend class="art-services__legend">When to charge</legend>
                <label class="art-services__option">
                    <input
                        v-model="draft.billing_mode"
                        type="radio"
                        :name="`art-services-mode-${planId}`"
                        value="immediate"
                        :data-testid="`sa-plan-card-art-services-mode-immediate-${planId}`"
                    />
                    Charge the distributor immediately
                </label>
                <label class="art-services__option">
                    <input
                        v-model="draft.billing_mode"
                        type="radio"
                        :name="`art-services-mode-${planId}`"
                        value="monthly"
                        :data-testid="`sa-plan-card-art-services-mode-monthly-${planId}`"
                    />
                    Add to the monthly subscription invoice
                </label>
            </fieldset>

            <div class="art-services__amounts">
                <label class="art-services__amount">
                    Digitizing, per job ($)
                    <input
                        v-model="draft.digitizing"
                        type="text"
                        inputmode="decimal"
                        :disabled="loading || saving"
                        :data-testid="`sa-plan-card-art-services-digitizing-${planId}`"
                    />
                </label>
                <label class="art-services__amount">
                    Vectorizing, per job ($)
                    <input
                        v-model="draft.vectorizing"
                        type="text"
                        inputmode="decimal"
                        :disabled="loading || saving"
                        :data-testid="`sa-plan-card-art-services-vectorizing-${planId}`"
                    />
                </label>
            </div>

            <p
                v-if="invalidMessage"
                class="art-services__error"
                :data-testid="`sa-plan-card-art-services-invalid-${planId}`"
            >
                {{ invalidMessage }}
            </p>

            <div class="art-services__actions">
                <button
                    type="button"
                    class="art-services__save"
                    :disabled="!canSave"
                    :data-testid="`sa-plan-card-art-services-save-${planId}`"
                    @click="onSave"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <!--
                  Appears only when the write landed: the admin's
                  confirmation, and what a ui-runner scenario waits on.
                -->
                <span
                    v-if="saved && !dirty"
                    class="art-services__saved"
                    :data-testid="`sa-plan-card-art-services-saved-${planId}`"
                >
                    Saved.
                </span>
            </div>

            <p
                v-if="error"
                class="art-services__error"
                :data-testid="`sa-plan-card-art-services-error-${planId}`"
            >
                {{ error }}
            </p>
        </template>
    </div>
</template>

<script>
import { dollarsToCents } from "@/utils/dollarsToCents";

/** Mirrors art_service_terms.MAX_AMOUNT_CENTS (the server enforces it too). */
export const MAX_AMOUNT_CENTS = 50000;

function centsToDollars(cents) {
    return Number.isInteger(cents) ? (cents / 100).toFixed(2) : "";
}

export default {
    name: "TierArtServicesSettings",

    props: {
        planId: { type: [Number, String], required: true },
        tierKey: { type: String, default: null },
        // { billing_mode, digitizing_cents, vectorizing_cents, is_configured }
        // for this tier, or null while loading / when the tier has no row
        // in the response.
        setting: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        saving: { type: Boolean, default: false },
        saved: { type: Boolean, default: false },
        error: { type: String, default: "" },
    },

    emits: ["save"],

    data() {
        return { draft: this.draftFrom(this.setting) };
    },

    computed: {
        digitizingCents() {
            return dollarsToCents(this.draft.digitizing);
        },
        vectorizingCents() {
            return dollarsToCents(this.draft.vectorizing);
        },
        invalidMessage() {
            const bad = [
                ["Digitizing", this.digitizingCents],
                ["Vectorizing", this.vectorizingCents],
            ].find(
                ([, c]) => c === null || c < 0 || c > MAX_AMOUNT_CENTS,
            );
            if (!bad) return "";
            return `${bad[0]} must be an amount from $0.00 to $${(
                MAX_AMOUNT_CENTS / 100
            ).toFixed(2)}.`;
        },
        dirty() {
            if (!this.setting) return false;
            return (
                this.draft.billing_mode !== this.setting.billing_mode ||
                this.digitizingCents !== this.setting.digitizing_cents ||
                this.vectorizingCents !== this.setting.vectorizing_cents
            );
        },
        canSave() {
            return (
                !!this.tierKey &&
                !!this.setting &&
                !this.loading &&
                !this.saving &&
                this.dirty &&
                !this.invalidMessage
            );
        },
    },

    watch: {
        // A new server row (load or a successful save) resets the draft. A
        // FAILED save does not change `setting`, so the admin's edit stays
        // for a retry.
        setting(next) {
            this.draft = this.draftFrom(next);
        },
    },

    methods: {
        draftFrom(setting) {
            return {
                billing_mode: setting ? setting.billing_mode : "immediate",
                digitizing: setting ? centsToDollars(setting.digitizing_cents) : "",
                vectorizing: setting ? centsToDollars(setting.vectorizing_cents) : "",
            };
        },
        onSave() {
            if (!this.canSave) return;
            this.$emit("save", this.tierKey, {
                tier_key: this.tierKey,
                billing_mode: this.draft.billing_mode,
                digitizing_cents: this.digitizingCents,
                vectorizing_cents: this.vectorizingCents,
            });
        },
    },
};
</script>

<style scoped>
.art-services__head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin: 18px 0 10px;
    flex-wrap: wrap;
}
.art-services__title {
    font-size: 13px;
    font-weight: 600;
    color: #2d3748;
}
.art-services__hint,
.art-services__empty {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.art-services__empty {
    margin: 0;
}
.art-services__modes {
    border: none;
    padding: 0;
    margin: 0 0 10px;
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
}
.art-services__legend {
    font-size: 12px;
    color: var(--tf-fg-4);
    margin-bottom: 4px;
}
.art-services__option,
.art-services__amount {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #2d3748;
}
.art-services__amounts {
    display: flex;
    gap: 18px;
    flex-wrap: wrap;
    margin-bottom: 10px;
}
.art-services__amount input {
    width: 80px;
    padding: 2px 6px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
}
.art-services__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}
.art-services__save {
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 4px;
    border: 1px solid #2b6cb0;
    background: #2b6cb0;
    color: #fff;
    cursor: pointer;
}
.art-services__save:disabled {
    opacity: 0.5;
    cursor: default;
}
.art-services__saved {
    font-size: 13px;
    color: #276749;
}
.art-services__error {
    font-size: 13px;
    color: #c53030;
    margin: 6px 0 0;
}
</style>
